'use client';

import { useCallback, useEffect, useState } from 'react';

import { useRouter } from 'next/navigation';

import { useCheckoutStore } from '@/stores';
import { ROUTES } from '@/types/enums';
import type { HighlightedRaffleDetails } from '@/types/models/HighlightedRaffleDetails';
import { calculatePackTotal, isRaffleOnSale, logger, type PromotionOffer } from '@/utils';

type UseHighlightedRaffleProps = {
  raffle: HighlightedRaffleDetails;
  packOffers: PromotionOffer[];
  promotionOffers: PromotionOffer[] | undefined;
  promotionOffersResolved?: boolean;
};

export const useHighlightedRaffle = ({
  raffle,
  packOffers,
  promotionOffers,
  promotionOffersResolved,
}: UseHighlightedRaffleProps) => {
  const router = useRouter();
  const { checkoutData, setPurchaseData, resetCheckout, clearPurchaseData } =
    useCheckoutStore();
  const [ticketQuantity, setTicketQuantity] = useState(
    raffle?.initialTicketsAmount ?? raffle?.minTicketsPerPurchase ?? 1,
  );
  const [hasQuantityError, setHasQuantityError] = useState(false);
  const [totalValue, setTotalValue] = useState(0);
  const [lastClickedPackQuantity, setLastClickedPackQuantity] = useState<number | null>(
    null,
  );

  useEffect(() => {
    if (raffle?.initialTicketsAmount) {
      setTicketQuantity(raffle.initialTicketsAmount);
    }
  }, [raffle?.initialTicketsAmount]);

  useEffect(() => {
    // Limpa o checkout persistido se nao bater com a rifa atual.
    const storedRaffleId = checkoutData?.purchaseData?.raffleId;
    if (!storedRaffleId || !raffle?.id) return;

    if (storedRaffleId !== raffle.id) {
      logger.warn('Clearing purchase data due to raffle mismatch', {
        source: 'details',
        storedRaffleId,
        currentRaffleId: raffle.id,
      });
      clearPurchaseData();
      return;
    }

    if (raffle.status && !isRaffleOnSale(raffle.status)) {
      logger.warn('Clearing purchase data due to raffle status', {
        source: 'details',
        raffleId: raffle.id,
        status: raffle.status,
      });
      clearPurchaseData();
    }
  }, [
    checkoutData?.purchaseData?.raffleId,
    raffle?.id,
    raffle?.status,
    clearPurchaseData,
  ]);

  useEffect(() => {
    const ticketPrice = raffle?.ticketPrice ?? 0;

    const total = calculatePackTotal({
      offers: packOffers,
      ticketsAmount: ticketQuantity,
      unitPrice: ticketPrice,
    });

    setTotalValue(total);
  }, [ticketQuantity, raffle, packOffers]);

  const minTickets = raffle?.minTicketsPerPurchase || 1;
  const maxTickets = raffle?.maxTicketsPerPurchase || 999999;

  const handleChangeQuantity = useCallback(
    (quantity: number) => {
      const quantityIsInvalid = quantity < minTickets || quantity > maxTickets;
      setHasQuantityError(quantityIsInvalid);
      setTicketQuantity(quantity);
    },
    [minTickets, maxTickets],
  );

  const addQuantity = useCallback(
    (value: number) => {
      handleChangeQuantity(Math.min(ticketQuantity + value, maxTickets));
    },
    [ticketQuantity, maxTickets, handleChangeQuantity],
  );

  const handleComboClick = useCallback(
    (pack: { quantity: number; percentage: number }) => {
      const isSamePack = pack.quantity === lastClickedPackQuantity;
      const newQuantity = isSamePack ? ticketQuantity + pack.quantity : pack.quantity;

      if (!isSamePack) {
        setLastClickedPackQuantity(pack.quantity);
      }

      handleChangeQuantity(Math.min(newQuantity, maxTickets));
    },
    [ticketQuantity, lastClickedPackQuantity, maxTickets, handleChangeQuantity],
  );

  const goToCheckout = useCallback(async () => {
    if (!raffle) return;
    if (!isRaffleOnSale(raffle.status)) return;

    resetCheckout();

    const baseData = {
      raffleId: raffle.id,
      ticketsAmount: ticketQuantity,
      ticketPrice: raffle.ticketPrice ?? 0,
      totalValue,
      raffleName: raffle.name,
      raffleTag: raffle.tag,
      drawDate: raffle.drawDate,
      minTicketsPerPurchase: raffle.minTicketsPerPurchase ?? 1,
      maxTicketsPerPurchase: raffle.maxTicketsPerPurchase ?? Number.MAX_SAFE_INTEGER,
    };

    const purchaseData = {
      ...baseData,
      prizeValue: raffle.prizeValue,
      isPromotionAvailable: raffle.isPromotionAvailable,
      promotionPercentage: raffle.promotionPercentage,
      promotionTicketsAmount: raffle.promotionTicketsAmount,
      promotionTitle: raffle.promotionTitle,
      promotionDescription: raffle.promotionDescription,
      promotionOffers,
      promotionOffersResolved: promotionOffersResolved ? true : undefined,
    };

    setPurchaseData(purchaseData);

    router.push(ROUTES.Enum['/checkout']);
  }, [
    raffle,
    ticketQuantity,
    totalValue,
    setPurchaseData,
    resetCheckout,
    router,
    promotionOffers,
    promotionOffersResolved,
  ]);

  return {
    ticketQuantity,
    totalValue,
    addQuantity,
    handleComboClick,
    handleChangeQuantity,
    goToCheckout,
    hasQuantityError,
  };
};
